import isFunction from 'lodash/isFunction';
import { Button, ButtonProps } from 'primereact/button';
import React, { useCallback } from 'react';
import { SegmentEventTypes } from '../../../data/tracking';
import Analytics from '../../../utils/Analytics';

export type AdlButtonVariant =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'ghost'
	| 'split';
export type AdlButtonType =
	| 'default'
	| 'inverse'
	| 'destructive'
	| 'upgrade'
	| 'disabled'
	| 'grey';
export type AdlButtonSize = 'standard' | 'expanded' | 'condensed';

export type AdlButtonProps = ButtonProps & {
	className?: string;
	variant?: AdlButtonVariant;
	type?: AdlButtonType;
	size?: AdlButtonSize;
	leftIcon?: string;
	rightIcon?: string;
	segmentvalue?: string;
	dataqa?: string;
	additionalIconClassName?: string;
};

/**
 * AdlButton is a comprehensive button component based on ADL design system.
 * It supports all button variants, types, sizes, and states.
 */
export const AdlButton: React.FC<AdlButtonProps> = props => {
	const {
		className = '',
		variant = 'primary',
		type = 'default',
		size = 'standard',
		leftIcon,
		rightIcon,
		dataqa: dataQa = 'ui-button-adl',
		additionalIconClassName,
		onClick,
		segmentvalue,
		children,
		disabled,
		...rest
	} = props;

	const isDisabled = disabled || props.type === 'disabled';

	// Helper function to convert icon name to CSS class
	const getIconClass = (iconName?: string): string => {
		if (!iconName) return '';
		// Convert to lowercase and replace spaces/hyphens with hyphens
		const normalizedName = iconName.toLowerCase().replace(/[\s-]+/g, '-');
		return `icon icon-${normalizedName}`;
	};

	const clickHandler = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			Analytics.trackSegmentEvent({
				trackingData: {
					field_type: 'button',
					field_name_dataqa: `ui-button-${dataQa}`,
					field_value: segmentvalue,
				},
				eventType: SegmentEventTypes.UserInteraction,
			});

			if (isFunction(onClick)) {
				onClick(e);
			}
		},
		[onClick, dataQa, segmentvalue]
	);

	// Build CSS classes
	const buttonClasses = [
		'adl-button',
		`${variant}`,
		`${type}`,
		`${size}`,
		isDisabled && 'adl-button disabled',
		className,
	]
		.filter(Boolean)
		.join(' ');

	// Render content
	const renderContent = () => {
		return (
			<>
				{leftIcon && (
					<span className={`adl-button left-icon ${getIconClass(leftIcon)}`} />
				)}
				{children && <span className="adl-button text">{children}</span>}
				{rightIcon && (
					<span
						className={`adl-button right-icon ${getIconClass(rightIcon)}`}
					/>
				)}
				{additionalIconClassName && (
					<span
						className={`adl-button additional-icon ${additionalIconClassName}`}
					/>
				)}
			</>
		);
	};

	return (
		<div className={`common-ui-button ${className}`} data-qa={dataQa}>
			<Button
				{...rest}
				className={buttonClasses}
				data-qa={`ui-button-${dataQa}`}
				onClick={clickHandler}
				disabled={isDisabled}
			>
				{renderContent()}
			</Button>
		</div>
	);
};
